import { useCallback, useMemo, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import { IconAddWindow, IconPlus } from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import { NavFilterLead, useFilterOpen } from '../layout/NavList/NavFilterLead'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { MultiSelect } from '../components/inputs'
import { GuideWindowBody } from '../components/guides/GuideWindowBody'
import { useGuides } from '../hooks/useGuides'
import { useTopics } from '../hooks/useTopics'
import { useModal } from '../context/ModalContext'
import { guideKind, guideOpenUrl } from '../services/guides'
import { filterAndRank } from '../utils/search'
import { topicLabelFor } from '../utils/topicLabel'
import './GuidesPage.css'
import { usePageTitle } from '../components/DocumentTitle'

const KIND_LABEL = { url: 'LINK', image: 'IMG', video: 'VID', pdf: 'PDF', file: 'FILE' }

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconEdit = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const IconDelete = () => (
  <svg {...svgProps}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
)

const IconExternal = () => (
  <svg {...svgProps}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

// Guides workspace: a searchable, topic-filterable list of the user's guides in
// the sidebar beside a tiled window grid. Clicking a guide opens it in the active
// viewer window (images / PDFs / video / links render adaptively).
function GuidesPage() {
  const { openModal } = useModal()
  const { guides, status, reload, removeGuide } = useGuides()
  const { topics: topicOptions } = useTopics()

  const [q, setQ] = useState('')
  const [topicFilter, setTopicFilter] = useState([])
  const [filterOpen, toggleFilter] = useFilterOpen('ct-guides-filter-open')
  const [notice, setNotice] = useState('')

  const grid = useWindowGrid({
    storageKey: 'ct-grid-guides-v1',
    limits: { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 },
    initialType: 'guide',
  })

  const guidesById = useMemo(() => {
    const map = new Map()
    guides.forEach((g) => map.set(g._id, g))
    return map
  }, [guides])

  const activeGuideId = useMemo(() => {
    for (const col of grid.columns) {
      const win = col.windows.find((w) => w.id === grid.activeId)
      if (win) return win.entityId
    }
    return null
  }, [grid.columns, grid.activeId])

  const topicIds = useMemo(() => topicFilter.map((t) => String(t._id)), [topicFilter])
  const visible = useMemo(() => {
    let list = guides
    if (topicIds.length) {
      const want = new Set(topicIds)
      list = list.filter((g) => (g.topics || []).some((t) => want.has(String(t?._id ?? t))))
    }
    return filterAndRank(list, q, (g) => g.title || g.name || '')
  }, [guides, q, topicIds])

  const openGuide = useCallback(
    (guide) => {
      if (!guide?._id) return
      grid.selectEntity({ type: 'guide', entityId: guide._id })
    },
    [grid]
  )

  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(
        `You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`
      )
      return
    }
    grid.addWindow({ type: 'guide' })
  }, [grid])

  const handleNew = () =>
    openModal('guide-form', {
      onSaved: (guide) => {
        reload()
        openGuide(guide)
      },
    })

  const handleEdit = useCallback(
    (guide) => openModal('guide-form', { guide, onSaved: reload }),
    [openModal, reload]
  )

  const clearWindowsForId = useCallback(
    (id) => {
      grid.columns.forEach((col) =>
        col.windows.forEach((w) => {
          if (String(w.entityId) === String(id)) grid.setWindowEntity(w.id, null)
        })
      )
    },
    [grid]
  )

  const handleDelete = useCallback(
    (guide) => {
      openModal('confirm', {
        title: 'Delete guide',
        message: `Delete "${guide.title || guide.name || 'this guide'}"? It will be removed everywhere it's attached.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => removeGuide(guide._id),
        onConfirmed: () => clearWindowsForId(guide._id),
      })
    },
    [openModal, removeGuide, clearWindowsForId]
  )

  const getTitle = useCallback(
    (win) => {
      if (!win.entityId) return 'New window'
      const guide = guidesById.get(win.entityId)
      return guide ? guide.title || guide.name || 'Guide' : status === 'loading' ? 'Loading…' : 'Guide'
    },
    [guidesById, status]
  )
  usePageTitle(activeGuideId ? getTitle({ entityId: activeGuideId }) : '')

  const renderWindow = useCallback(
    (win) => {
      const guide = win.entityId ? guidesById.get(win.entityId) : null
      return (
        <GuideWindowBody
          guide={guide || null}
          guideId={win.entityId}
          loading={status === 'loading' && Boolean(win.entityId) && !guide}
        />
      )
    },
    [guidesById, status]
  )

  const renderMenuItems = useCallback(
    (win) => {
      const guide = win.entityId ? guidesById.get(win.entityId) : null
      if (!guide) return null
      return (
        <>
          <button
            type="button"
            role="menuitem"
            onClick={() => window.open(guideOpenUrl(guide), '_blank', 'noopener')}
          >
            <IconExternal />
            Open in new tab
          </button>
          <button type="button" role="menuitem" onClick={() => handleEdit(guide)}>
            <IconEdit />
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() => handleDelete(guide)}
          >
            <IconDelete />
            Delete
          </button>
        </>
      )
    },
    [guidesById, handleEdit, handleDelete]
  )

  return (
    <MainLayout
      storageId="guides-layout"
      navLocation={<NavLocation title="Guides" />}
      navActions={[
        { key: 'add-window', label: 'Add window', icon: <IconAddWindow />, onClick: handleAddWindow },
        { key: 'new', label: 'New guide', icon: <IconPlus />, onClick: handleNew },
      ]}
      navDock={
        <GridDock
          items={grid.minimized}
          getTitle={getTitle}
          onRestore={grid.restoreWindow}
          onClose={grid.closeWindow}
        />
      }
      nav={
        <NavList>
          <div className="guides-nav">
            {topicOptions.length ? (
              <NavFilterLead
                open={filterOpen}
                active={topicIds.length > 0}
                onToggle={toggleFilter}
                onClear={() => setTopicFilter([])}
                clearDisabled={!topicIds.length}
              />
            ) : null}
            <div
              className={
                'guides-filters' +
                (topicOptions.length ? ' is-under-lead' : '') +
                (topicOptions.length && filterOpen ? ' is-with-panel' : ' is-last')
              }
            >
              <input
                className="ct-input"
                type="text"
                value={q}
                placeholder="Search guides…"
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            {topicOptions.length && filterOpen ? (
              <div className="nav-topic-filter">
                <span className="nav-filter-label">Topics</span>
                <MultiSelect
                  value={topicFilter}
                  onChange={setTopicFilter}
                  options={topicOptions}
                  getOptionValue={(t) => t._id}
                  getOptionLabel={topicLabelFor(topicOptions)}
                  placeholder="Any topic"
                  clearQueryOnSelect={false}
                />
              </div>
            ) : null}

            {status === 'loading' ? (
              <div className="guides-nav-status">Loading…</div>
            ) : status === 'error' ? (
              <div className="guides-nav-status">Could not load guides.</div>
            ) : visible.length === 0 ? (
              <div className="guides-nav-status">
                {q || topicIds.length ? 'No guides match.' : 'No guides yet. Create one.'}
              </div>
            ) : (
              <div className="guides-nav-list">
                {visible.map((guide) => (
                  <div
                    key={guide._id}
                    className={
                      'guides-nav-row' + (String(guide._id) === String(activeGuideId) ? ' is-selected' : '')
                    }
                  >
                    <button
                      type="button"
                      className="guides-nav-open"
                      title={guide.title || guide.name}
                      onClick={() => openGuide(guide)}
                    >
                      <span className="guides-nav-kind">{KIND_LABEL[guideKind(guide)] || 'FILE'}</span>
                      <span className="guides-nav-name">
                        {guide.title || guide.name || 'Untitled guide'}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="guides-nav-act"
                      title="Edit guide"
                      aria-label={`Edit ${guide.title || guide.name || 'guide'}`}
                      onClick={() => handleEdit(guide)}
                    >
                      <IconEdit />
                    </button>
                    <button
                      type="button"
                      className="guides-nav-act is-danger"
                      title="Delete guide"
                      aria-label={`Delete ${guide.title || guide.name || 'guide'}`}
                      onClick={() => handleDelete(guide)}
                    >
                      <IconDelete />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </NavList>
      }
      display={
        <div className="sr-grid-wrap">
          {notice && <div className="sr-grid-notice">{notice}</div>}
          <div className="sr-grid-body">
            <WindowGrid
              grid={grid}
              renderWindow={renderWindow}
              getTitle={getTitle}
              renderMenuItems={renderMenuItems}
              empty="Select a guide from the list to view it."
            />
          </div>
        </div>
      }
    />
  )
}

export default GuidesPage
